import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { resenasApi } from '../api/resenas'
import { fmtFecha, fmtFechaHora, fmtMonto, toISOLocal } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { colors, font, text, radius, sx, shadow } from '../design/tokens'
import Boton from '../components/Boton'

/**
 * Reseñas de clientes — CLIENTES → Reseñas.
 *
 * NO es una bandeja de reclamos (Martin rechazó seis maquetas de eso el 28 ago
 * 2026). Es LA LECTURA de cómo se está atendiendo a la gente:
 *  · están TODOS — los que puntúan bien no se ocultan, la proporción ES el dato;
 *  · una encuesta por vez (entrega / compra preguntan cosas distintas y no se
 *    mezclan nunca); cada solapa abre con SU número (la entrega de 5, el NPS)
 *    y el de toda la experiencia va arriba, fuera de las solapas;
 *  · cada pregunta con su resultado Y cuántos la contestaron;
 *  · todo medidor es clickeable y baja a las personas: click en "No", en un
 *    valor del reparto o en un fletero → la lista queda filtrada (29 ago 2026);
 *  · UNA LÍNEA POR RESEÑA, cerrada por defecto (Martin, 29 ago): el comentario
 *    ENTERO —nunca cortado— y lo que contestó en chips; click → la traza del
 *    pedido (venta → remito → entrega → reseña) y la huella (aviso / atendida).
 *  · compra siempre en la escala 0-10 del índice (opción A, 29 ago): las nuevas
 *    guardan nps = nps5×2 y el click del cliente se dice en el tooltip.
 * Atender una reseña mala sigue existiendo, pero como acción dentro de la
 * reseña: no organiza ni parte la pantalla.
 */

// Las encuestas de esta pantalla. La de EVENTOS no está y no va (Martin, 28
// ago): vive aparte, con su propia página de resultados — acá queda el link.
const ENCUESTAS = [
  { id: 'entrega', label: 'Entrega' },
  { id: 'compra', label: 'Compra' },
]

const RANGOS = [
  { dias: 7, label: '7 días' },
  { dias: 30, label: '30 días' },
  { dias: 60, label: '60 días' },
  { dias: 365, label: '12 meses' },
]

// UNA sola escala de color en toda la pantalla, la de Encuesta::banda():
// 4-5 buena · 3 regular · 1-2 mala (en 0-10: ≥7 · 5-6 · ≤4).
const COLOR_BANDA = { buena: colors.progreso, regular: colors.warnStrong, mala: colors.err }
const colorValor = (v, de = 5) => { const x = de === 10 ? v / 2 : v; return x >= 4 ? colors.progreso : x >= 3 ? colors.warnStrong : colors.err }

const hace = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return toISOLocal(d) }
const esSi = (v) => v === true || v === 'si' || v === 'sí'
const n2 = (v) => (v == null ? '—' : String(Math.round(v * 100) / 100))

// Los filtros que nacen de un click en un medidor. Uno solo a la vez.
const FILTROS = {
  valor: (clave, valor, label) => ({ key: `${clave}=${valor}`, label: `${label}: ${valor}`, fn: v => v.respuestas?.[clave] != null && Number(v.respuestas[clave]) === valor }),
  sino: (clave, si, label) => ({ key: `${clave}=${si ? 'si' : 'no'}`, label: `${label} ${si ? 'Sí' : 'No'}`, fn: v => v.respuestas?.[clave] != null && esSi(v.respuestas[clave]) === si }),
  quien: (nombre) => ({ key: `quien=${nombre}`, label: `Entregó ${nombre}`, fn: v => v.quien === nombre }),
  nps: (banda) => ({
    key: `nps=${banda}`,
    label: banda === 'prom' ? 'Experiencia excelente' : banda === 'pas' ? 'Estuvo bien' : 'No estuvo a la altura',
    fn: v => { const n = Number(v.respuestas?.nps); return v.respuestas?.nps != null && (banda === 'prom' ? n >= 9 : banda === 'pas' ? (n >= 7 && n <= 8) : n <= 6) },
  }),
  atender: () => ({ key: 'atender', label: 'Para atender', fn: v => v.para_atender }),
}

export default function ResenasPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const idDestacado = Number(params.get('id')) || null

  const [tipoGuardado, setTipo] = useStoredState('lgr.resenas.tipo', 'entrega')
  const tipo = ENCUESTAS.some(e => e.id === tipoGuardado) ? tipoGuardado : 'entrega'
  const [diasGuardado, setDias] = useStoredState('lgr.resenas.dias', 60)
  const dias = RANGOS.some(r => r.dias === diasGuardado) ? diasGuardado : 60
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState(null)
  const [atendiendo, setAtendiendo] = useState(null)
  const [nota, setNota] = useState('')
  const [trabajando, setTrabajando] = useState(null)
  const [abiertas, setAbiertas] = useState(() => new Set(idDestacado ? [idDestacado] : []))
  const [aviso, setAviso] = useState('')
  const destacadoRef = useRef(null)

  const desde = useMemo(() => hace(dias), [dias])
  const hasta = useMemo(() => toISOLocal(new Date()), [])

  const cargar = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await resenasApi.lectura({ tipo, desde, hasta })
      setData(data); setError('')
    } catch (e) {
      if (!silent) setError(e.response?.data?.message || 'No se pudieron cargar las reseñas.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [tipo, desde, hasta])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar(true), 30000, atendiendo === null)

  useEffect(() => {
    if (!aviso) return undefined
    const t = setTimeout(() => setAviso(''), 4000)
    return () => clearTimeout(t)
  }, [aviso])

  // Llegar desde el aviso emergente (?id=): abrir la encuesta donde vive esa
  // reseña. Puede ser de otro tipo del que quedó guardado, o más vieja que el
  // rango elegido — en los dos casos se ajusta la pantalla para que se vea.
  useEffect(() => {
    if (!idDestacado) return
    resenasApi.ubicacion(idDestacado).then(({ data }) => {
      setTipo(data.tipo)
      const antiguedad = Math.ceil((Date.now() - new Date(data.respondida_at).getTime()) / 86400000)
      setDias(prev => (antiguedad > prev ? (RANGOS.find(r => r.dias > antiguedad)?.dias || 365) : prev))
    }).catch(() => {})
  }, [idDestacado, setTipo, setDias])

  useEffect(() => {
    if (idDestacado && destacadoRef.current) destacadoRef.current.scrollIntoView({ block: 'center' })
  }, [idDestacado, data])

  const cambiarTipo = (id) => { setTipo(id); setBusca(''); setFiltro(null); setAtendiendo(null) }
  const cambiarDias = (d) => { setDias(d); setFiltro(null) }
  const toggleFiltro = (f) => setFiltro(prev => (prev && prev.key === f.key ? null : f))
  const toggleAbierta = (id) => setAbiertas(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })

  const atender = async (v) => {
    const n = nota.trim()
    if (!n) { setAviso('Escribí una línea: qué hiciste con el cliente.'); return }
    setTrabajando(v.id)
    try {
      await resenasApi.atender(v.id, n)
      setAtendiendo(null); setNota('')
      setAviso(`Listo — queda quién la atendió, cuándo y qué hizo con ${v.cliente || 'el cliente'}.`)
      await cargar(true)
    } catch (e) {
      const d = e.response?.data
      setAviso(d?.errors ? Object.values(d.errors).flat().join(' ') : (d?.message || 'No se pudo marcar como atendida.'))
    } finally { setTrabajando(null) }
  }

  // El chip "N para atender" lleva a la solapa que las tiene (las de esta si hay).
  const irAParaAtender = () => {
    if (filtro?.key === 'atender') { setFiltro(null); return }
    if (!(data?.voces || []).some(v => v.para_atender)) cambiarTipo(tipo === 'compra' ? 'entrega' : 'compra')
    setFiltro(FILTROS.atender())
  }

  const voces = useMemo(() => {
    let lista = data?.voces || []
    if (filtro) lista = lista.filter(filtro.fn)
    const q = busca.trim().toLowerCase()
    if (q) lista = lista.filter(v => textoBusqueda(v).toLowerCase().includes(q))
    return lista
  }, [data, busca, filtro])

  const conteos = data?.conteos || {}
  const exp = data?.experiencia
  const pendientes = data?.para_atender_total || 0
  const filtrando = !!(filtro || busca.trim())

  return (
    <div>
      <div style={st.head}>
        <h1 style={st.h1}>⭐ Cómo nos están puntuando</h1>
        <div style={st.rango}>
          <span style={st.rangoL}>Rango</span>
          {RANGOS.map(r => (
            <button
              key={r.dias} type="button" aria-pressed={dias === r.dias}
              style={{ ...st.preset, ...(dias === r.dias ? st.presetOn : {}) }}
              onClick={() => cambiarDias(r.dias)}
            >{r.label}</button>
          ))}
          <span style={st.rangoFechas}>{desde.split('-').reverse().join('/')} → {hasta.split('-').reverse().join('/')}</span>
        </div>
      </div>

      {/* El número de TODA la casa va fuera de las solapas: no cambia al cambiar de encuesta. */}
      {data && (
        <div style={st.general}>
          {exp ? (
            <>
              <span style={st.generalV}>{n2(exp.promedio)}</span>
              <span style={st.generalK}>Experiencia general</span>
              <span>de 5 · <b style={st.generalB}>{(exp.promedio * 2).toFixed(2)}</b> de 10</span>
              <span style={st.generalSub}>
                Promedio de las <b style={st.generalSubB}>{exp.respuestas}</b> respuestas puntuadas del período — compra <b style={st.generalSubB}>{n2(exp.compra.promedio)}</b> ({exp.compra.respuestas}) · entrega <b style={st.generalSubB}>{n2(exp.entrega.promedio)}</b> ({exp.entrega.respuestas}), todo junto
              </span>
            </>
          ) : <span style={st.generalSub}>Nadie contestó ninguna encuesta en el período.</span>}
          {pendientes > 0 && (
            <button type="button" style={st.chipAtender} onClick={irAParaAtender} title="Regulares o malas que nadie marcó como atendidas">
              {pendientes} para atender
            </button>
          )}
        </div>
      )}

      {error && <div style={st.error}>{error}</div>}

      <div style={st.card}>
        <div style={st.tabs}>
          {ENCUESTAS.map(e => (
            <button
              key={e.id} type="button" aria-pressed={tipo === e.id}
              style={{ ...st.tab, ...(tipo === e.id ? st.tabOn : {}) }}
              onClick={() => cambiarTipo(e.id)}
            >
              {e.label}
              {conteos[e.id] > 0 && <span style={st.tabN}>{conteos[e.id]}</span>}
            </button>
          ))}
          {(data?.otras_encuestas || []).map(o => (
            <a key={o.ruta} href={o.ruta} target="_blank" rel="noreferrer" style={st.otraEncuesta}>{o.label} ↗</a>
          ))}
        </div>

        {aviso && <div style={st.avisoBar}>{aviso}</div>}

        {loading ? <p style={st.vacio}>Cargando…</p> : !data ? null : data.respondidas === 0 ? (
          <p style={st.vacio}>Nadie contestó esta encuesta en el período.</p>
        ) : (
          <>
            {data.tipo === 'entrega' && <CabezaEntrega d={data} filtro={filtro} onFiltro={toggleFiltro} />}
            {data.tipo === 'compra' && <CabezaCompra d={data} filtro={filtro} onFiltro={toggleFiltro} />}
            <Preguntas d={data} filtro={filtro} onFiltro={toggleFiltro} />
            {data.quienes.length > 0 && (
              <div style={st.bloque}>
                <p style={st.bloqueH}>Quién hizo la entrega</p>
                <div style={{ display: 'grid', gap: 5 }}>
                  {data.quienes.map(q => {
                    const on = filtro?.key === `quien=${q.nombre}`
                    const c = q.promedio == null ? colors.textMeta : colorValor(q.promedio, q.de)
                    return (
                      <div key={q.nombre} style={{ ...st.fila, ...(on ? st.filaOn : {}) }} title="Ver sus reseñas" onClick={() => toggleFiltro(FILTROS.quien(q.nombre))}>
                        <span style={st.filaN}>{q.nombre}</span>
                        <span style={{ ...st.filaV, color: c }}>{n2(q.promedio)}</span>
                        <span style={st.filaBar}><span style={{ ...st.filaBarIn, width: `${q.promedio ? (q.promedio / q.de) * 100 : 0}%`, background: q.promedio == null ? colors.textPlh : c }} /></span>
                        <span style={st.filaSub}>{q.n} {q.n === 1 ? 'reseña' : 'reseñas'}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div style={st.vocesHead}>
              <span style={st.vocesT}>Reseña por reseña</span>
              <span style={st.cuenta}>{filtrando ? `${voces.length} de ${data.voces.length}` : data.voces.length}</span>
              {filtro && (
                <span style={st.filtroChip}>
                  {filtro.label}
                  <button type="button" style={st.filtroX} onClick={() => setFiltro(null)} title="Quitar filtro">✕</button>
                </span>
              )}
              <input
                type="search" value={busca} onChange={e => setBusca(e.target.value)}
                data-nocaps placeholder="Buscar en comentarios y clientes…" style={st.buscar}
              />
            </div>

            {voces.length === 0 ? (
              <p style={st.vacio}>{busca.trim() ? 'Nadie dijo eso.' : 'Ninguna reseña con ese filtro en el período.'}</p>
            ) : voces.map(v => (
              <div key={v.id} ref={v.id === idDestacado ? destacadoRef : null}>
                <Voz
                  v={v} tipo={data.tipo} destacada={v.id === idDestacado} busca={busca.trim()}
                  desplegada={abiertas.has(v.id)} onDesplegar={() => toggleAbierta(v.id)}
                  abierta={atendiendo === v.id} nota={nota} setNota={setNota}
                  onAbrir={() => { setAtendiendo(v.id); setNota('') }}
                  onCancelar={() => { setAtendiendo(null); setNota('') }}
                  onAtender={() => atender(v)}
                  ocupada={trabajando === v.id}
                  navigate={navigate}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Cabeza de cada encuesta: SU número ──────────────────────────────────

function Reparto({ items, total, clave, label, filtro, onFiltro, colorDe = (v) => colorValor(v) }) {
  return (
    <div style={st.reparto}>
      {items.map(r => {
        const on = filtro?.key === `${clave}=${r.valor}`
        return (
          <div
            key={r.valor} style={{ ...st.rf, ...(on ? st.rfOn : {}), cursor: r.n ? 'pointer' : 'default' }}
            title={r.n ? `Ver quiénes pusieron ${r.valor}` : ''}
            onClick={() => r.n && onFiltro(FILTROS.valor(clave, r.valor, label))}
          >
            <span style={{ ...st.rfV, color: r.n ? colorDe(r.valor) : colors.textPlh }}>{r.valor}</span>
            <span style={st.rfBar}><span style={{ ...st.rfBarIn, width: `${total ? (r.n / total) * 100 : 0}%`, background: colorDe(r.valor) }} /></span>
            <span style={{ ...st.rfN, color: r.n ? colors.textSub : colors.textPlh }}>{r.n}</span>
          </div>
        )
      })}
    </div>
  )
}

function CabezaEntrega({ d, filtro, onFiltro }) {
  const g = d.general
  return (
    <div style={st.cabeza}>
      {g && (
        <>
          <div style={st.hero}>
            <span style={{ ...st.heroV, color: colorValor(g.promedio, g.de) }}>{n2(g.promedio)}</span>
            <span style={st.heroU}>de {g.de}</span>
            <span style={st.heroK}>La entrega</span>
          </div>
          <div style={{ minWidth: 210 }}>
            <Reparto items={g.reparto} total={g.contestaron} clave="general" label="La entrega" filtro={filtro} onFiltro={onFiltro} />
          </div>
        </>
      )}
      <div style={st.cabezaPie}>
        <span><b style={st.pieB}>{d.respondidas}</b> respondieron</span>
        <span style={st.pieM}>La contestan en el portal al recibir el pedido — no se manda por mail</span>
      </div>
    </div>
  )
}

/** Un renglón de la leyenda del NPS: cuántos, y click para verlos. */
function Leyenda({ banda, n, texto, sub, color, filtro, onFiltro }) {
  return (
    <div style={{ ...st.ley, ...(filtro?.key === `nps=${banda}` ? st.leyOn : {}), cursor: n ? 'pointer' : 'default' }} onClick={() => n && onFiltro(FILTROS.nps(banda))} title={n ? 'Ver quiénes' : ''}>
      <b style={{ ...st.leyB, color: n ? color : colors.textPlh }}>{n}</b>
      <span style={n ? {} : st.apagado}>{texto}</span> <span style={st.leyS}>{sub}</span>
    </div>
  )
}

function CabezaCompra({ d, filtro, onFiltro }) {
  const i = d.indice
  const enviadas = d.enviadas
  return (
    <div style={st.cabeza}>
      {i && (
        <>
          <div style={st.hero}>
            <span style={{ ...st.heroV, color: i.nps >= 50 ? colors.progreso : i.nps >= 0 ? colors.warnStrong : colors.err }}>{i.nps}</span>
            <span style={st.heroK}>índice de experiencia</span>
          </div>
          <div style={{ display: 'grid', gap: 3 }}>
            {/* La pregunta pasó de "¿nos recomendarías?" a "¿cómo fue tu
                experiencia?" (Martin, 3 sep 2026). El índice conserva la
                fórmula NPS de siempre (mejores − peores) para que el número
                histórico siga comparable; cambió lo que se pregunta. */}
            <Leyenda banda="prom" n={i.promotores} texto="excelente" sub="(5★ · antes 9 y 10)" color={colors.ok} filtro={filtro} onFiltro={onFiltro} />
            <Leyenda banda="pas" n={i.pasivos} texto="estuvo bien" sub="(4★ · antes 7 y 8)" color={colors.ok} filtro={filtro} onFiltro={onFiltro} />
            <Leyenda banda="det" n={i.detractores} texto="no estuvo a la altura" sub="(1 a 3★ · antes 0 a 6)" color={colors.err} filtro={filtro} onFiltro={onFiltro} />
          </div>
        </>
      )}
      <div style={st.cabezaPie}>
        <span>
          <b style={st.pieB}>{d.respondidas}</b> respondieron
          {enviadas > 0 && <> de <b style={st.pieB}>{enviadas}</b> mails enviados · <b style={st.pieB}>{Math.round((d.respondidas / enviadas) * 100)} %</b></>}
        </span>
        <span style={st.pieM}>Se manda por mail a las 48 h de la compra</span>
      </div>
    </div>
  )
}

// ─── Pregunta por pregunta: cuántos eligieron cada respuesta ────────────
//
// El promedio solo NO refleja las respuestas (Martin, 28 ago): 5+5+1 y 4+4+3
// promedian casi igual y son cosas distintas. Va el reparto entero, y cada
// renglón se clickea para ver a quiénes.

function Preguntas({ d, filtro, onFiltro }) {
  if (!d.preguntas.length) return null

  return (
    <div style={{ ...st.preguntas, gridTemplateColumns: `repeat(${Math.min(d.preguntas.length, 4)}, minmax(0, 1fr))` }}>
      {d.preguntas.map(p => (
        <div key={p.clave} style={st.pq}>
          <span style={st.pqK}>{p.label}</span>

          {p.forma === 'escala' && (
            <span style={{ ...st.pqV, color: p.promedio == null ? colors.textPlh : colorValor(p.promedio, p.de) }}>
              {p.promedio == null ? '—' : <>{n2(p.promedio)}<span style={st.pqU}>de {p.de}</span></>}
            </span>
          )}
          {p.forma === 'si_no' && (
            <span style={{ ...st.pqV, color: p.no === 0 ? colors.progreso : colorValor(p.promedio ?? 0) }}>
              {p.si}<span style={st.pqU}>de {p.contestaron} · sí</span>
            </span>
          )}

          {p.forma === 'escala' && (
            <Reparto items={p.reparto} total={p.contestaron} clave={p.clave} label={p.label} filtro={filtro} onFiltro={onFiltro} colorDe={(v) => colorValor(v, p.de)} />
          )}

          {p.forma === 'si_no' && (
            <div style={st.reparto}>
              {[['Sí', p.si, colors.progreso, true], ['No', p.no, colors.err, false]].map(([lbl, n, c, si]) => {
                const on = filtro?.key === `${p.clave}=${si ? 'si' : 'no'}`
                return (
                  <div
                    key={lbl} style={{ ...st.rf, gridTemplateColumns: '18px minmax(0, 1fr) 26px', ...(on ? st.rfOn : {}), cursor: n ? 'pointer' : 'default' }}
                    title={n ? `Ver quiénes contestaron ${lbl}` : ''}
                    onClick={() => n && onFiltro(FILTROS.sino(p.clave, si, p.label))}
                  >
                    <span style={{ ...st.rfV, color: n === 0 ? colors.textPlh : c }}>{lbl}</span>
                    <span style={st.rfBar}><span style={{ ...st.rfBarIn, width: `${p.contestaron ? (n / p.contestaron) * 100 : 0}%`, background: c }} /></span>
                    <span style={{ ...st.rfN, color: n === 0 ? colors.textPlh : colors.textSub }}>{n}</span>
                  </div>
                )
              })}
            </div>
          )}

          <span style={st.pqN}>
            Contestaron {p.contestaron}
            {p.sin_contestar > 0 && ` · ${p.sin_contestar} la dejaron en blanco`}
            {p.promedio != null && (
              <> · <span style={st.pqProm} title={p.forma === 'si_no' ? 'Sí = 5 y No = 1, la misma escala 1 a 5 de las demás preguntas, para poder compararlas' : 'Promedio de las respuestas'}>promedio {n2(p.promedio)}</span></>
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── Una reseña = una línea, cerrada por defecto ─────────────────────────

/** Lo que se busca: el comentario, el nombre y la nota de atención. */
function textoBusqueda(v) {
  return [v.comentario, v.no_gusto, v.cliente, v.atendida?.nota].filter(Boolean).join(' ')
}

/** Resalta TODAS las apariciones del término — sin tocar lo que dijo el cliente. */
function marcar(txt, busca) {
  if (!busca || !txt) return txt
  const partes = []
  const low = txt.toLowerCase(), q = busca.toLowerCase()
  let i = 0, k = 0
  while (true) {
    const j = low.indexOf(q, i)
    if (j < 0) { partes.push(txt.slice(i)); break }
    partes.push(txt.slice(i, j), <mark key={k++} style={st.marca}>{txt.slice(j, j + q.length)}</mark>)
    i = j + q.length
  }
  return partes
}

function Chips({ v, todas }) {
  const tags = todas ? v.tags : v.tags.filter(t => t.estado !== 'vacio')
  if (!tags.length) return null
  return (
    <div style={st.chips}>
      {tags.map((t, i) => (
        <span key={i} style={{ ...st.chip, ...(t.estado === 'no' ? st.chipNo : t.estado === 'si' ? st.chipSi : t.estado === 'vacio' ? st.chipVacio : {}) }}>{t.label}</span>
      ))}
    </div>
  )
}

function Voz({ v, tipo, destacada, busca, desplegada, onDesplegar, abierta, nota, setNota, onAbrir, onCancelar, onAtender, ocupada, navigate }) {
  const color = COLOR_BANDA[v.banda] || colors.textSub
  const escribio = !!(v.comentario || v.no_gusto)
  const texto = v.no_gusto && v.comentario ? `${v.no_gusto} — ${v.comentario}` : (v.no_gusto || v.comentario || '')
  const contesto = v.tags.some(t => t.estado !== 'vacio')
  const parar = (e) => e.stopPropagation()

  return (
    <div
      style={{ ...st.voz, ...(destacada ? st.vozDestacada : {}) }}
      title={desplegada ? 'Click para plegar' : 'Click para ver el pedido'}
      onClick={onDesplegar} role="button" tabIndex={0}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onDesplegar() } }}
    >
      <span style={st.vozFecha}>{fmtFechaHora(v.respondida_at)}</span>
      {tipo === 'compra' ? (
        <span style={{ ...st.vozP, color }} title={v.nps5 != null ? `Marcó ${v.nps5} de 5 (= ${v.nps} en la escala 0-10 del NPS)` : 'Escala 0-10'}>
          {v.nps ?? '—'}<span style={st.vozPd}>NPS</span>
        </span>
      ) : (
        <span style={{ ...st.vozP, color }}>{v.puntaje ?? '—'}<span style={st.vozPd}>de 5</span></span>
      )}
      <span style={st.vozNom} title={v.cliente || ''}>{marcar(v.cliente || '—', busca)}</span>
      <div style={st.vozTxt}>
        {escribio && <div style={st.vozQ}>“{marcar(texto, busca)}”</div>}
        {(escribio || contesto) ? <Chips v={v} todas={desplegada} /> : <div style={st.vozSolo}>Solo puntuó: no contestó el resto de las preguntas.</div>}
      </div>
      <span style={st.vozQuien}>
        {v.documento_ruta && <button type="button" style={st.linkBtn} onClick={e => { parar(e); navigate(v.documento_ruta) }}>{v.documento}</button>}
        {v.quien && <span style={st.vozQuienN} title={v.quien}>{v.quien}</span>}
      </span>
      <span style={st.vozAccion} onClick={parar}>
        {v.para_atender && !abierta && <Boton variante="success" sm disabled={ocupada} onClick={onAbrir}>✔ Atendida</Boton>}
        {v.atendida && !desplegada && <span style={st.vozHecha} title={`Atendida por ${v.atendida.por} — “${v.atendida.nota}”`}>✔ atendida</span>}
      </span>

      {desplegada && (
        <div style={st.detalle}>
          <Pedido p={v.pedido} respondida={v.respondida_at} navigate={navigate} />
          {(v.aviso?.a || v.atendida) && (
            <div style={st.huella}>
              {v.aviso?.a && (
                <span>
                  Aviso a <b style={st.huellaB}>{v.aviso.a}</b> · {fmtFechaHora(v.aviso.cuando)}
                  {v.aviso.leido ? <> · leído {fmtFechaHora(v.aviso.leido)}</> : <> · <b style={{ color: colors.warn }}>sin leer</b></>}
                </span>
              )}
              {v.atendida && (
                <span style={st.atendida}>
                  ✔ Atendida por <b style={st.huellaB}>{v.atendida.por}</b> el {fmtFechaHora(v.atendida.el)} — “{v.atendida.nota}”
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {abierta && (
        <div style={st.atender} onClick={parar}>
          <input
            autoFocus data-nocaps style={st.atenderInput}
            placeholder={`¿Qué hiciste con ${v.cliente || 'el cliente'}? Una línea.`}
            value={nota} onChange={e => setNota(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onAtender(); if (e.key === 'Escape') onCancelar() }}
          />
          <Boton variante="success" sm disabled={ocupada} onClick={onAtender}>{ocupada ? '…' : '✔ Atendida'}</Boton>
          <Boton variante="ghost" sm onClick={onCancelar}>Cancelar</Boton>
        </div>
      )}
    </div>
  )
}

/**
 * La trazabilidad del pedido detrás de la reseña: de la venta a la entrega,
 * con las fechas reales, para poder ver el POR QUÉ de la respuesta. Lo que no
 * existe no se muestra (ni un guion inventado).
 */
function Pedido({ p, respondida, navigate }) {
  if (!p || (!p.nv && !p.remito && !p.entrega)) return null
  const ir = (ruta) => (e) => { e.stopPropagation(); navigate(ruta) }
  const dias = (a, b) => {
    if (!a || !b) return null
    const d = Math.round((new Date(b) - new Date(a)) / 86400000)
    return d === 0 ? 'mismo día' : d === 1 ? '1 día' : `${d} días`
  }
  const demora = dias(p.entrega?.cuando, respondida)

  return (
    <div style={st.pedido}>
      {p.nv && (
        <span style={st.paso}>
          <b style={st.pasoK}>Venta</b>
          <button type="button" style={st.pasoLink} onClick={ir(p.nv.ruta)}>{p.nv.numero}</button>
          {p.nv.fecha && <span style={{ ...st.pasoD, ...sx.mono }}>{fmtFecha(p.nv.fecha)}</span>}
          {p.nv.items > 0 && <span style={st.pasoD}>{p.nv.items} {p.nv.items === 1 ? 'renglón' : 'renglones'}</span>}
          {p.nv.total > 0 && <span style={{ ...st.pasoD, ...sx.mono }}>${fmtMonto(p.nv.total)}</span>}
          {p.nv.origen && <span style={st.pasoD}>{p.nv.origen}</span>}
          {p.nv.factura && <span style={st.pasoD}>factura <span style={sx.mono}>{p.nv.factura}</span></span>}
        </span>
      )}
      {p.remito && (
        <span style={st.paso}>
          <b style={st.pasoK}>Remito</b>
          <button type="button" style={st.pasoLink} onClick={ir(p.remito.ruta)}>{p.remito.numero}</button>
          {p.remito.emitido && <span style={st.pasoD}>emitido <span style={sx.mono}>{fmtFecha(p.remito.emitido)}</span></span>}
          {p.remito.estado && <span style={st.pasoD}>{p.remito.estado}</span>}
          {p.remito.retira ? <span style={st.pasoD}>retira el cliente</span> : p.remito.zona && <span style={st.pasoD}>{p.remito.zona}</span>}
        </span>
      )}
      {p.entrega && (
        <span style={st.paso}>
          <b style={st.pasoK}>Entrega</b>
          {p.entrega.salida && <button type="button" style={st.pasoLink} onClick={ir(`/salidas/${p.entrega.salida}`)}>salida #{p.entrega.salida}</button>}
          {p.entrega.fletero && <span style={st.pasoD}>{p.entrega.fletero}</span>}
          {p.entrega.cuando && <span style={{ ...st.pasoD, ...sx.mono }}>{fmtFechaHora(p.entrega.cuando)}</span>}
          {p.entrega.receptor && <span style={st.pasoD}>recibió {p.entrega.receptor}</span>}
          {p.entrega.motivo && <span style={{ ...st.pasoD, color: colors.err }}>{p.entrega.motivo}</span>}
        </span>
      )}
      {demora && <span style={st.paso}><b style={st.pasoK}>Reseña</b><span style={st.pasoD}>{demora} después de la entrega</span></span>}
    </div>
  )
}

const st = {
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' },
  h1: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: colors.textStrong },
  rango: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
  rangoL: { fontSize: text.sec, color: colors.textSec },
  preset: { border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSub, borderRadius: radius.pill, fontFamily: 'inherit', fontSize: text.sec, fontWeight: 600, padding: '3px 10px', cursor: 'pointer' },
  presetOn: { background: colors.text, borderColor: colors.text, color: colors.bg },
  rangoFechas: { ...sx.mono, fontSize: text.sec, color: colors.textMeta, marginLeft: 4 },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.md, padding: '8px 12px', marginBottom: 12, fontSize: text.body },
  card: { background: colors.bg, borderRadius: 12, boxShadow: shadow.card, overflow: 'hidden' },

  general: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', margin: '0 0 14px', color: colors.textSub, fontSize: text.body },
  generalV: { ...sx.mono, fontSize: 22, fontWeight: 700, color: colors.ok, lineHeight: 1, letterSpacing: '-0.02em' },
  generalK: { fontSize: text.meta, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textSec },
  generalB: { ...sx.mono, color: colors.text },
  generalSub: { color: colors.textMeta, fontSize: text.sec },
  generalSubB: { ...sx.mono, color: colors.textSub, fontWeight: 600 },
  chipAtender: { marginLeft: 'auto', background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.pill, fontSize: text.sec, fontWeight: 700, padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit' },

  tabs: { display: 'flex', padding: '0 12px', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, overflowX: 'auto' },
  tab: { appearance: 'none', border: 'none', borderBottom: '3px solid transparent', marginBottom: -1, background: 'none', fontFamily: 'inherit', fontSize: text.body, fontWeight: 700, color: colors.textSec, padding: '10px 15px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' },
  tabOn: { color: colors.text, borderBottomColor: colors.text, background: colors.bg, textTransform: 'uppercase', letterSpacing: '0.03em' },
  otraEncuesta: { marginLeft: 'auto', alignSelf: 'center', fontSize: text.sec, color: colors.link, textDecoration: 'none', fontWeight: 600, padding: '0 6px', whiteSpace: 'nowrap' },
  tabN: { ...sx.mono, fontSize: text.meta, fontWeight: 700, background: colors.border, color: colors.textSub, borderRadius: radius.pill, padding: '1px 7px' },
  avisoBar: { background: colors.infoBg, color: colors.info, padding: '8px 18px', fontSize: text.sec, borderBottom: `1px solid ${colors.borderSoft}` },

  cabeza: { display: 'flex', alignItems: 'center', gap: 26, padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' },
  hero: { display: 'flex', alignItems: 'baseline', gap: 8 },
  heroV: { ...sx.mono, fontSize: 46, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' },
  heroU: { fontSize: 14, color: colors.textMeta },
  heroK: { fontSize: text.meta, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textSec },
  ley: { fontSize: text.body, color: colors.textSub, cursor: 'pointer', padding: '1px 4px', marginLeft: -4, borderRadius: radius.xs },
  leyOn: { background: colors.linkBg },
  leyB: { ...sx.mono, fontSize: 14, marginRight: 4 },
  leyS: { color: colors.textMeta, fontSize: text.meta },
  apagado: { color: colors.textPlh },
  cabezaPie: { marginLeft: 'auto', display: 'grid', gap: 3, textAlign: 'right', fontSize: text.sec, color: colors.textSub },
  pieB: { ...sx.mono, color: colors.text },
  pieM: { color: colors.textMeta, fontSize: text.meta },

  preguntas: { display: 'grid', borderBottom: `1px solid ${colors.border}` },
  pq: { padding: '12px 18px', borderRight: `1px solid ${colors.borderSoft}` },
  pqK: { display: 'block', fontSize: text.meta, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec, marginBottom: 5 },
  pqV: { ...sx.mono, fontSize: 19, fontWeight: 700, lineHeight: 1 },
  pqU: { fontFamily: font.sans, fontSize: text.sec, color: colors.textMeta, fontWeight: 500, marginLeft: 4 },
  pqN: { display: 'block', fontSize: text.meta, color: colors.textMeta, marginTop: 6 },
  pqProm: { cursor: 'help', borderBottom: `1px dotted ${colors.textMeta}` },
  // Reparto de respuestas: valor · barra · cuántos. Las tres columnas son
  // obligatorias — sin grilla, el valor y la cantidad se leen pegados ("5 15"
  // como "515"), que es exactamente el bug que vio Martin en producción.
  reparto: { display: 'grid', gap: 3, marginTop: 8 },
  rf: { display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr) 26px', gap: 8, alignItems: 'center', borderRadius: radius.xs, padding: '1px 4px', margin: '0 -4px' },
  rfOn: { background: colors.linkBg },
  rfV: { ...sx.mono, fontSize: text.meta, fontWeight: 700, textAlign: 'right' },
  rfBar: { height: 7, borderRadius: 4, background: colors.borderSoft, overflow: 'hidden' },
  rfBarIn: { display: 'block', height: '100%' },
  rfN: { ...sx.mono, fontSize: text.meta, fontWeight: 700, textAlign: 'right' },

  bloque: { padding: '13px 18px', borderBottom: `1px solid ${colors.border}` },
  bloqueH: { margin: '0 0 10px', fontSize: text.meta, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textSec },
  fila: { display: 'grid', gridTemplateColumns: '160px 46px minmax(0, 1fr) 80px', gap: 12, alignItems: 'center', fontSize: text.sec, cursor: 'pointer', borderRadius: radius.xs, padding: '2px 6px', margin: '0 -6px' },
  filaOn: { background: colors.linkBg },
  filaN: { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  filaV: { ...sx.mono, fontWeight: 700 },
  filaBar: { height: 8, borderRadius: 4, background: colors.borderSoft, overflow: 'hidden' },
  filaBarIn: { display: 'block', height: '100%' },
  filaSub: { fontSize: text.sec, color: colors.textMeta, textAlign: 'right', whiteSpace: 'nowrap' },

  vocesHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: `1px solid ${colors.border}`, background: colors.bgSoft, flexWrap: 'wrap' },
  vocesT: { fontSize: text.meta, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textSub },
  cuenta: { ...sx.mono, fontSize: text.sec, color: colors.textSec },
  filtroChip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: colors.linkBg, border: `1px solid ${colors.linkBorder}`, color: colors.link, borderRadius: radius.pill, padding: '2px 6px 2px 10px', fontSize: text.sec, fontWeight: 600 },
  filtroX: { border: 'none', background: 'none', color: colors.link, cursor: 'pointer', fontSize: text.body, padding: '0 3px', lineHeight: 1, fontFamily: 'inherit' },
  buscar: { ...sx.input, marginLeft: 'auto', height: 28, padding: '0 9px', fontSize: text.sec, width: 260 },

  voz: { display: 'grid', gridTemplateColumns: '112px 58px 200px minmax(0, 1fr) 150px 92px', gap: 12, padding: '8px 18px', borderBottom: `1px solid ${colors.borderSoft}`, alignItems: 'start', cursor: 'pointer' },
  vozDestacada: { background: colors.linkBg },
  vozFecha: { ...sx.mono, fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap', paddingTop: 2 },
  vozP: { ...sx.mono, fontSize: text.body, fontWeight: 700, whiteSpace: 'nowrap' },
  vozPd: { fontFamily: font.sans, fontSize: text.meta, fontWeight: 500, color: colors.textMeta, marginLeft: 4 },
  vozNom: { fontSize: text.body, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  vozTxt: { minWidth: 0, display: 'grid', gap: 4 },
  // El comentario va ENTERO, con sus saltos de línea: nunca puntos suspensivos.
  vozQ: { fontSize: text.body, color: colors.text, lineHeight: 1.45, whiteSpace: 'pre-line' },
  vozSolo: { fontSize: text.sec, color: colors.textSec },
  chips: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  chip: { display: 'inline-block', fontSize: text.meta, fontWeight: 600, color: colors.textSub, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.xs, padding: '1px 6px', lineHeight: 1.4, whiteSpace: 'nowrap' },
  chipNo: { color: colors.err, background: colors.errBg, borderColor: 'transparent' },
  chipSi: { color: colors.ok, background: colors.okBg, borderColor: 'transparent' },
  chipVacio: { color: colors.textMeta, background: colors.bg, borderStyle: 'dashed' },
  vozQuien: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  vozQuienN: { fontSize: text.meta, color: colors.textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  vozAccion: { display: 'flex', justifyContent: 'flex-end', cursor: 'default' },
  vozHecha: { fontSize: text.meta, color: colors.ok, whiteSpace: 'nowrap', paddingTop: 2 },
  linkBtn: { border: 'none', background: 'none', padding: 0, color: colors.link, cursor: 'pointer', ...sx.mono, fontSize: text.meta, textAlign: 'left' },
  detalle: { gridColumn: '4 / -1', display: 'grid', gap: 6, cursor: 'default' },
  pedido: { display: 'grid', gap: 3, padding: '8px 10px', background: colors.bgSoft, borderRadius: radius.sm, fontSize: text.sec },
  paso: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' },
  pasoK: { fontSize: text.meta, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textSec, minWidth: 56 },
  pasoD: { color: colors.textSub },
  pasoLink: { border: 'none', background: 'none', padding: 0, color: colors.link, cursor: 'pointer', ...sx.mono, fontSize: text.sec, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 },
  marca: { background: '#fef08a', borderRadius: 2, color: colors.text },
  huella: { fontSize: text.sec, color: colors.textSec, display: 'grid', gap: 2 },
  huellaB: { color: colors.textSub, fontWeight: 600 },
  atendida: { color: colors.ok },
  atender: { gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 2, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: '8px 12px', cursor: 'default' },
  atenderInput: { ...sx.input, flex: 1, minWidth: 240, height: 30, padding: '0 10px', fontSize: text.sec },
  vacio: { padding: '28px 18px', color: colors.textMeta, textAlign: 'center', fontSize: text.body, margin: 0 },
}
